import { FC, useState } from 'react';
import { IonButton } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { BottomSheetModal } from '../../components/BottomSheetModal';
import { isPlatform } from '../../utils/isPlatform';
import { MicrophoneIcon, UploadIcon, KeyboardIcon } from '../../theme/Icons';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { RecordingState } from '../../recording/RecordingState';
import { useUploadAudio } from '../../recording/audio-upload/useUploadAudio';
import { ICareMapNoteTarget } from '../model/ICareMapNoteTarget';
import { AddNoteButton } from './AddNoteButton';

const useStyles = createUseStyles({
    content: {
        padding: '28px 24px 32px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        backgroundColor: '#fff'
    },
    title: {
        fontSize: 20,
        fontWeight: 600,
        color: 'var(--ion-color-dark)',
        marginBottom: 4,
        textAlign: 'center',
        letterSpacing: 'normal'
    },
    subtitle: {
        fontSize: 14,
        color: '#687f87',
        lineHeight: 1.5,
        marginBottom: 12,
        textAlign: 'center',
        letterSpacing: 'normal'
    },
    button: {
        '--padding-top': '20px',
        '--padding-bottom': '20px',
        '--padding-start': '20px',
        '--padding-end': '20px',
        height: 'auto',
        margin: 0
    },
    buttonInner: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        textAlign: 'left'
    },
    buttonIcon: {
        flexShrink: 0
    },
    buttonText: {
        flex: 1
    },
    buttonLabel: {
        fontSize: 16,
        fontWeight: 600,
        marginBottom: 2,
        letterSpacing: 'normal'
    },
    buttonDescription: {
        fontSize: 13,
        fontWeight: 400,
        opacity: 0.85,
        textTransform: 'none',
        whiteSpace: 'normal',
        lineHeight: 1.4,
        letterSpacing: 'normal'
    }
});

export interface INoteCreationChooserProps {
    canAdd: boolean;
    // existing "add note" behavior (opens the editor) — wired to "Type it"
    onTypeIt: () => void;
    // who the note is for; Voice + Upload land on this target. Defaults to the care recipient.
    noteTarget?: ICareMapNoteTarget;
    useIcon?: boolean;
    iconOnly?: boolean;
}

type INoteCreationMethod = 'Voice' | 'Upload Audio' | 'Type';

export const NoteCreationChooser: FC<INoteCreationChooserProps> = ({ canAdd, onTypeIt, noteTarget, useIcon, iconOnly }) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);

    const mixpanel = useInstance(MixPanelEventEmitter);
    const recordingState = useInstance(RecordingState);
    const { startUpload, uploadUI } = useUploadAudio({ noteTarget, onFilesSelected: () => setIsOpen(false) });

    // Upload Audio is hidden on iOS: the OS file picker can't reach Voice Memos (app-private) without
    // manual "Save to Files" steps, and shows Photos/Camera options for a web <input>. Android's file
    // picker (Storage Access Framework) is a real audio browser, so it stays there (and on web).
    const showUploadAudio = !isPlatform('ios');

    // keepOpen is for Upload: the chooser stays up while the native file picker is presented and
    // closes only once a file is chosen (see onFilesSelected), so cancelling returns to the chooser.
    const choose = (method: INoteCreationMethod, action: () => void, keepOpen = false) => {
        if (!keepOpen) {
            setIsOpen(false);
        }
        mixpanel.track('Note Creation Initiated', () => ({ 'Method': method }));
        action();
    };

    return (
        <>
            <AddNoteButton
                canAdd={canAdd}
                useIcon={useIcon}
                iconOnly={iconOnly}
                onClick={() => setIsOpen(true)}
            />

            <BottomSheetModal
                isOpen={isOpen}
                onDidDismiss={() => setIsOpen(false)}
                data-id="note-creation-chooser"
            >
                <div className={classes.content}>
                    <div className={classes.title}>New Note</div>
                    <div className={classes.subtitle}>How would you like to start?</div>

                    <IonButton
                        expand="block"
                        color="primary"
                        className={classes.button}
                        data-id="create-note-voice"
                        onClick={() => choose('Voice', () => recordingState.triggerRecording(noteTarget ? { noteTarget } : undefined))}
                    >
                        <div className={classes.buttonInner}>
                            <div className={classes.buttonIcon}><MicrophoneIcon color="light" customSize={22} /></div>
                            <div className={classes.buttonText}>
                                <div className={classes.buttonLabel}>Voice</div>
                                <div className={classes.buttonDescription}>Record now and we'll summarize.</div>
                            </div>
                        </div>
                    </IonButton>

                    {showUploadAudio && (
                        <IonButton
                            expand="block"
                            fill="outline"
                            color="primary"
                            className={classes.button}
                            data-id="create-note-upload"
                            onClick={() => choose('Upload Audio', startUpload, true)}
                        >
                            <div className={classes.buttonInner}>
                                <div className={classes.buttonIcon}><UploadIcon color="primary" customSize={22} /></div>
                                <div className={classes.buttonText}>
                                    <div className={classes.buttonLabel}>Upload Audio</div>
                                    <div className={classes.buttonDescription}>Pick a file and get a summary.</div>
                                </div>
                            </div>
                        </IonButton>
                    )}

                    <IonButton
                        expand="block"
                        fill="outline"
                        color="primary"
                        className={classes.button}
                        data-id="create-note-type"
                        onClick={() => choose('Type', onTypeIt)}
                    >
                        <div className={classes.buttonInner}>
                            <div className={classes.buttonIcon}><KeyboardIcon color="primary" customSize={22} /></div>
                            <div className={classes.buttonText}>
                                <div className={classes.buttonLabel}>Type</div>
                                <div className={classes.buttonDescription}>Enter it as text.</div>
                            </div>
                        </div>
                    </IonButton>
                </div>
            </BottomSheetModal>

            {showUploadAudio && uploadUI}
        </>
    );
};
